import type { ColDef, ICellRendererParams } from "@ag-grid-community/core";
import { Button, Icon } from "@blueprintjs/core";
import { createColDef } from "@package/ui-apollo-components";
import {
  useGetMediaGridQuery,
  type Media,
} from "@package/ui-gql/index-data-access";
import { useMemo } from "react";
import { SidebarEntryPath } from "../../../../constants/contants";
import { setItemGridQuery } from "../../../../store/slices/slice-item-grid";
import { setSelectedParentIdHierarchy } from "../../../../store/slices/slice-media";
import { useAppDispatch, useAppSelector } from "../../../../store/store";
import { ItemGrid } from "../../../ItemGrid/ItemGrid";

export function MediaGridStandalone() {
  const dispatch = useAppDispatch();
  const selectedParentIdHierarchy = useAppSelector(
    (state) => state.media.selectedParentIdHierarchy,
  );
  const variables = useAppSelector((state) => state.itemGrid.media.variables);

  const {
    data: dataMedia,
    loading: loadingMedia,
    error: errorMedia,
  } = useGetMediaGridQuery({
    variables: {
      ...variables,
      filter: {
        ...variables?.filter,
        parent: selectedParentIdHierarchy.slice(-1)?.[0],
      },
    },
    skip: !selectedParentIdHierarchy?.length,
  });

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        ...createColDef<Media>("isDir", "", 0, 50),
        flex: undefined,
        resizable: false,
        maxWidth: 50,
        cellRenderer: (params: ICellRendererParams<Media, boolean>) => (
          <Icon icon={params.value ? "folder-close" : "media"} />
        ),
      },
      {
        ...createColDef<Media>("name", "Name", 3, 200),
        cellRenderer: (params: ICellRendererParams<Media, string>) => {
          if (params.data?.isDir) {
            return (
              <div className="flex flex-col justify-center h-full">
                <div className="flex max-h-8">
                  <Button
                    minimal
                    small
                    disabled={!params.data?.isDir}
                    alignText="left"
                    intent="none"
                    text={params.value}
                    onClick={() => {
                      const id = params.data?.id;
                      if (id) {
                        dispatch(
                          setItemGridQuery({
                            path: SidebarEntryPath.media,
                            query: "",
                          }),
                        );
                        dispatch(
                          setSelectedParentIdHierarchy([
                            ...selectedParentIdHierarchy,
                            id,
                          ]),
                        );
                      }
                    }}
                  />
                </div>
              </div>
            );
          }

          return <div className="pl-1.5">{params.value}</div>;
        },
      },
      {
        ...createColDef<Media>("size", "Size", 1, 100),
        flex: undefined,
        resizable: false,
        maxWidth: 100,
      },
      createColDef<Media>("updatedAt", "Last Updated", 1, 125),
    ],
    [dispatch, selectedParentIdHierarchy],
  );

  return (
    <ItemGrid
      columnDefs={columnDefs}
      sidebarEntryPath={SidebarEntryPath.media}
      data={dataMedia}
      error={!!errorMedia}
      loading={!dataMedia && loadingMedia}
    />
  );
}
